import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
    useForm,
} from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import {
    BusinessInput,
    ProductInput,
    ProductSubtypeInput,
    ProductTypeInput,
} from '@barboxx/admin-components';
import {
    CREATE_BUSINESS_DEFAULT_PRODUCT_SCHEMA,
    useBusinessSelector,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessDefaultProduct } from '@barboxx/models';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateBusinessDefaultProductDialog = ({
    onClose,
    onSuccess,
    open,
}: Props) => {
    const { businessId } = useBusinessSelector();
    const notifications = useNotifications();

    const formContext = useForm<BusinessDefaultProduct.CreateRequest>({
        resolver: yupResolver(CREATE_BUSINESS_DEFAULT_PRODUCT_SCHEMA),
        defaultValues: {
            isActive: true,
            businessId,
        },
    });

    const mutateCreateBusinessDefaultProduct = useMutation({
        mutationKey: ['businessDefaultProduct'],
        mutationFn: (data: BusinessDefaultProduct.CreateRequest) =>
            Api.BusinessSpecific.DefaultProduct.postDefaultProduct(data),
    });

    const onSubmit = async (values: BusinessDefaultProduct.CreateRequest) => {
        return mutateCreateBusinessDefaultProduct
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created !`, {
                    severity: 'success',
                });
                onClose();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating default product!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Create Default Product
            </DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <BusinessInput />
                    <ProductInput />
                    <ProductTypeInput />
                    <ProductSubtypeInput />
                    <TextFieldElement label="Notes" name="notes" fullWidth />
                    <CheckboxElement label="Active" name="isActive" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
